
import { MovieFilters as MovieFiltersType } from '../types/movie';
import { Search, Filter, X, Sparkles } from 'lucide-react';

interface MovieFiltersProps {
  filters: MovieFiltersType;
  onFiltersChange: (filters: MovieFiltersType) => void;
  availableGenres: string[];
}

export const MovieFilters: React.FC<MovieFiltersProps> = ({
  filters,
  onFiltersChange,
  availableGenres
}) => {
  const updateFilter = (key: keyof MovieFiltersType, value: any) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const clearFilters = () => {
    onFiltersChange({
      search: '',
      scoreAbove7: false,
      gf: false,
      genres: [],
      director: '',
      sortBy: 'year',
      sortOrder: 'desc'
    });
  };

  const hasActiveFilters = filters.search || 
    filters.scoreAbove7 || 
    filters.genres.length > 0 || 
    filters.director || 
    filters.gf;

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-3 sm:p-4 lg:p-6 mb-4 sm:mb-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 space-y-3 sm:space-y-0">
        <h2 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center space-x-2 sm:space-x-3">
          <div className="p-1.5 sm:p-2 bg-primary-100 rounded-lg">
            <Filter className="h-4 w-4 sm:h-5 sm:w-5 text-primary-600" />
          </div>
          <span>Filters</span>
        </h2>
        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="self-start sm:self-auto text-sm text-gray-500 hover:text-red-600 flex items-center space-x-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg hover:bg-red-50 transition-colors"
          >
            <X className="h-4 w-4" />
            <span>Clear all</span>
          </button>
        )}
      </div>

      {/* Main Filters Grid */}
      <div className="space-y-4 sm:space-y-6">
        {/* Search */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2 sm:mb-3">
            Search
          </label>
          <div className="relative">
            <Search className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
            <input
              type="text"
              value={filters.search}
              onChange={(e) => updateFilter('search', e.target.value)}
              placeholder="Search by title or director..."
              className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 bg-gray-50 hover:bg-white focus:bg-white text-sm sm:text-base"
            />
          </div>
        </div>
      </div>

      {/* Quick Filters */}
      <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl border border-gray-100">
        <h3 className="text-sm font-semibold text-gray-700 mb-3 sm:mb-4 flex items-center space-x-2">
          <Sparkles className="h-4 w-4 text-primary-600" />
          <span>Quick Filters</span>
        </h3>
        <div className="space-y-3 sm:space-y-0 sm:grid sm:grid-cols-1 md:grid-cols-2 sm:gap-4">
          <div className={`flex items-center space-x-2 sm:space-x-3 p-2.5 sm:p-3 rounded-lg border transition-all duration-200 ${
            filters.scoreAbove7 
              ? 'bg-primary-50 border-primary-300 shadow-sm' 
              : 'bg-white border-gray-200 hover:border-primary-300'
          }`}>
            <input
              type="checkbox"
              id="scoreAbove7"
              checked={filters.scoreAbove7}
              onChange={(e) => updateFilter('scoreAbove7', e.target.checked)}
              className="h-4 w-4 sm:h-5 sm:w-5 accent-primary-600 text-primary-600 focus:ring-primary-500 border-gray-300 rounded transition-colors"
            />
            <label htmlFor="scoreAbove7" className="text-sm font-medium text-gray-700 cursor-pointer">
              Movies with rating above 7
            </label>
          </div>
          
          <div className={`flex items-center space-x-2 sm:space-x-3 p-2.5 sm:p-3 rounded-lg border transition-all duration-200 ${
            filters.gf 
              ? 'bg-primary-50 border-primary-300 shadow-sm' 
              : 'bg-white border-gray-200 hover:border-primary-300'
          }`}>
            <input
              type="checkbox"
              id="gf"
              checked={filters.gf}
              onChange={(e) => updateFilter('gf', e.target.checked)}
              className="h-4 w-4 sm:h-5 sm:w-5 accent-primary-600 text-primary-600 focus:ring-primary-500 border-gray-300 rounded transition-colors"
            />
            <label htmlFor="gf" className="text-sm font-medium text-gray-700 cursor-pointer">
              Seen with GF
            </label>
          </div>
        </div>
      </div>

      {/* Genres */}
      <div className="mt-4 sm:mt-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-3 sm:mb-4">Genres</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 sm:gap-3">
          {availableGenres.map((genre) => (
            <button
              key={genre}
              onClick={() => {
                const newGenres = filters.genres.includes(genre)
                  ? filters.genres.filter(g => g !== genre)
                  : [...filters.genres, genre];
                updateFilter('genres', newGenres);
              }}
              className={`px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm rounded-lg border transition-all duration-200 ${
                filters.genres.includes(genre)
                  ? 'bg-primary-100 border-primary-300 text-primary-700 shadow-sm'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:border-primary-300 hover:bg-primary-50'
              }`}
            >
              {genre}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
